import { EmptyCase, Input, Screen, Select } from '../../components';
import { useCreateOnlineSession, useDeleteOnlineSession, useGetAllOnlineSessions } from '../../services/onlineSession';

import './OnlineSessionsPage.scss';
import { Modal, Skeleton } from 'antd';
import { useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { RoleEnum } from '../../types/role';
import { parseSelect } from '../../utils/parseSelect';
import { useGetTopicsBySubject } from '../../services/topic';
import { useGetSubjectsByBranch } from '../../services/subject';
import { type IOnlineSession } from '../../types/onlineSession';
import { format } from 'date-fns';
import { IconTrash, IconPlus, IconVideo, IconChevronRight, IconAlertTriangle, IconUsers } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

const OnlineSessionsPage = () => {
  const { theme } = useTheme();
  const { loading, onlineSessions, refetch } = useGetAllOnlineSessions();
  const { createOnlineSession, loading: creating } = useCreateOnlineSession();
  const { deleteOnlineSession, loading: deleting } = useDeleteOnlineSession();
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();

  const isAdmin = (user?.role?.name === RoleEnum.superadmin || user?.role?.name === RoleEnum.admin);

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [editedSubject, setEditedSubject] = useState<string>('');
  const [editedTopic, setEditedTopic] = useState<string>('');
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  const { subjects } = useGetSubjectsByBranch();
  const { topics } = useGetTopicsBySubject(editedSubject);

  const toggleBlock = (block: string) => {
    setExpandedBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(block)) {
        newSet.delete(block);
      } else {
        newSet.add(block);
      }
      return newSet;
    });
  };

  const toggleTopic = (block: string, topic: string) => {
    const key = `${block}-${topic}`;
    setExpandedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const parsedSubjects = useMemo(() => parseSelect({
    array: subjects || [],
    labelKey: 'name',
    valueKey: '_id'
  }), [subjects]);

  const parsedTopics = useMemo(() => parseSelect({
    array: topics || [],
    labelKey: 'name',
    valueKey: '_id'
  }), [topics]);

  const handleCancel = () => {
    setVisible(false);
    setTitle('');
    setUrl('');
  };

  const handleSave = async () => {
    await createOnlineSession({ title, url, topic: editedTopic, subject: editedSubject });
    await refetch();
    handleCancel();
  };

  const handleDeleteClick = (id: string) => {
    setSessionToDelete(id);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (sessionToDelete) {
      await deleteOnlineSession(sessionToDelete);
      await refetch();
      setDeleteModalVisible(false);
      setSessionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setSessionToDelete(null);
  };

  const actions = isAdmin ? [{
    icon: (
      <IconPlus
        size={20}
        className="text-white"
        strokeWidth={2.5}
      />
    ),
    onClick: () => setVisible(true)
  }] : [];

  return (
    <Screen id="online-sessions" title="Clases online" actions={actions}>
      {loading ? (
        <div className="w-full space-y-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className={clsx(
                'rounded-xl border-2 p-4 sm:p-6',
                'bg-white border-gray-200 shadow-md',
                'dark:bg-surface-dark dark:border-borders dark:shadow-lg'
              )}
            >
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          ))}
        </div>
      ) : !onlineSessions || Object.keys(onlineSessions).length === 0 ? (
        <EmptyCase size={100} text="No se han encontrado clases" />
      ) : (
        <div className="w-full space-y-4">
          {Object.entries(onlineSessions).map(([bloc, blocContent]) => {
            const isBlockExpanded = expandedBlocks.has(bloc);

            return (
              <div
                key={bloc}
                className={clsx(
                  'rounded-xl border-2 transition-all duration-300 overflow-hidden',
                  'bg-white border-gray-200 shadow-md',
                  'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
                  'hover:shadow-lg dark:hover:shadow-xl'
                )}
              >
                <button
                  onClick={() => toggleBlock(bloc)}
                  className={clsx(
                    'w-full p-4 sm:p-6 flex items-center justify-between transition-all duration-300',
                    'hover:bg-gray-50 dark:hover:bg-surface-medium',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    'dark:focus:ring-offset-surface-dark'
                  )}
                >
                  <h3 className={clsx(
                    'text-lg sm:text-xl font-bold transition-colors duration-300',
                    'text-gray-900 dark:text-white'
                  )}>
                    {bloc}
                  </h3>
                  <div className={clsx(
                    'transition-transform duration-300',
                    isBlockExpanded && 'rotate-90'
                  )}>
                    <IconChevronRight
                      size={24}
                      className={clsx(
                        'transition-colors duration-300',
                        'text-gray-600 dark:text-blue-400'
                      )}
                    />
                  </div>
                </button>

                {isBlockExpanded && (
                  <div className={clsx(
                    'px-4 sm:px-6 pt-4 pb-4 sm:pb-6 space-y-3',
                    'border-t-2 border-gray-200 dark:border-borders',
                    'transition-all duration-300 ease-in-out'
                  )}>
                    {Object.entries(blocContent as any).map(([topic, topicContent]) => {
                      const topicKey = `${bloc}-${topic}`;
                      const isTopicExpanded = expandedTopics.has(topicKey);

                      return (
                        <div
                          key={topic}
                          className={clsx(
                            'rounded-lg border-2 transition-all duration-300 overflow-hidden',
                            'bg-gray-50 border-gray-200',
                            'dark:bg-blue-900/10 dark:border-blue-800/30',
                            'hover:shadow-sm'
                          )}
                        >
                          <button
                            onClick={() => toggleTopic(bloc, topic)}
                            className={clsx(
                              'w-full p-3 sm:p-4 flex items-center justify-between transition-all duration-300',
                              'hover:bg-gray-100 dark:hover:bg-blue-900/20',
                              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                              'dark:focus:ring-offset-surface-medium'
                            )}
                          >
                            <h4 className={clsx(
                              'font-semibold text-sm sm:text-base transition-colors duration-300',
                              'text-gray-900 dark:text-white'
                            )}>
                              {topic}
                            </h4>
                            <div className={clsx(
                              'transition-transform duration-300',
                              isTopicExpanded && 'rotate-90'
                            )}>
                              <IconChevronRight
                                size={20}
                                className={clsx(
                                  'transition-colors duration-300',
                                  'text-gray-600 dark:text-blue-400'
                                )}
                              />
                            </div>
                          </button>

                          {isTopicExpanded && (
                            <div className={clsx(
                              'pt-4 px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 border-t-2',
                              'border-gray-200 dark:border-borders',
                              'transition-all duration-300 ease-in-out'
                            )}>
                              {(topicContent as IOnlineSession[]).map((onlineSession) => (
                                <div
                                  key={onlineSession._id}
                                  className={clsx(
                                    'group relative overflow-hidden rounded-xl border-2 transition-all duration-300',
                                    'bg-white border-gray-200 shadow-sm',
                                    'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
                                    'hover:shadow-lg dark:hover:shadow-xl',
                                    'hover:border-blue-400 dark:hover:border-blue-500'
                                  )}
                                >
                                  {/* Gradient accent bar */}
                                  <div className={clsx(
                                    'absolute top-0 left-0 right-0 h-1.5',
                                    'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500',
                                    'dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400'
                                  )} />

                                  <div className="p-4 sm:p-5">
                                    <div className="flex flex-col sm:flex-row items-start gap-4">
                                      {/* Icon section */}
                                      <div className={clsx(
                                        'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300',
                                        'bg-gradient-to-br from-blue-500 to-purple-500',
                                        'dark:from-blue-600 dark:to-purple-600',
                                        'group-hover:shadow-lg',
                                        'shadow-md'
                                      )}>
                                        <IconVideo size={24} className="text-white" strokeWidth={2} />
                                      </div>

                                      {/* Content section */}
                                      <div className="flex-1 min-w-0 w-full">
                                        {/* Title and date */}
                                        <div className="mb-3 sm:mb-2">
                                          <h4 className={clsx(
                                            'text-base sm:text-lg font-bold transition-colors duration-300 mb-2',
                                            'text-gray-900 dark:text-white',
                                            'group-hover:text-blue-600 dark:group-hover:text-blue-400',
                                            'line-clamp-2'
                                          )}>
                                            {onlineSession.title}
                                          </h4>
                                          {/* Date badge */}
                                          <div className="flex items-center gap-3">
                                            <div className={clsx(
                                              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                                              'bg-gray-100 text-gray-700',
                                              'dark:bg-blue-900/30 dark:text-blue-200 dark:border dark:border-blue-800/50'
                                            )}>
                                              <span>{format(new Date(onlineSession.createdAt), 'dd/MM/yyyy')}</span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Actions - full width on mobile, inline on desktop */}
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                          <a
                                            href={onlineSession.url}
                                            target='_blank'
                                            rel="noreferrer"
                                            className={clsx(
                                              'relative inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300',
                                              'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
                                              'dark:from-blue-500 dark:to-indigo-500',
                                              'hover:from-blue-700 hover:to-indigo-700',
                                              'dark:hover:from-blue-600 dark:hover:to-indigo-600',
                                              'shadow-md hover:shadow-lg',
                                              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                              'dark:focus:ring-offset-surface-dark',
                                              'overflow-hidden group',
                                              'w-full sm:w-auto'
                                            )}
                                          >
                                            {/* Shine effect on hover */}
                                            <span className={clsx(
                                              'absolute inset-0 -translate-x-full group-hover:translate-x-full',
                                              'bg-gradient-to-r from-transparent via-white/20 to-transparent',
                                              'transition-transform duration-700 ease-in-out'
                                            )} />
                                            <IconUsers size={18} strokeWidth={2.5} className="relative z-10" />
                                            <span className="relative z-10">Unirse a la clase</span>
                                          </a>
                                          {isAdmin && (
                                            <button
                                              onClick={() => handleDeleteClick(onlineSession._id)}
                                              className={clsx(
                                                'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-300',
                                                'bg-red-600 text-white hover:bg-red-700',
                                                'dark:bg-red-500 dark:hover:bg-red-600',
                                                'shadow-md hover:shadow-lg',
                                                'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                                                'dark:focus:ring-offset-surface-dark',
                                                'w-full sm:w-auto sm:px-3 sm:py-2'
                                              )}
                                              title="Eliminar clase"
                                            >
                                              <IconTrash size={18} strokeWidth={2} />
                                              <span className="sm:hidden">Eliminar</span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {visible && (

        <Modal
          title={
            <div className={clsx(
              'flex items-center gap-3',
              'text-gray-900 dark:text-white'
            )}>
              <div className={clsx(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                'bg-gradient-to-br from-blue-500 to-purple-500',
                'dark:from-blue-600 dark:to-purple-600'
              )}>
                <IconVideo size={20} className="text-white" />
              </div>
              <span className="text-xl font-bold">Crear clase online</span>
            </div>
          }
          open={visible}
          onCancel={handleCancel}
          onOk={handleSave}
          okButtonProps={{
            disabled: !title || !url || !editedSubject || !editedTopic,
            loading: creating,
            className: clsx(
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'border-none shadow-md hover:shadow-lg',
              'transition-all duration-300'
            )
          }}
          cancelButtonProps={{
            className: clsx(
              'border-gray-300 dark:border-gray-600',
              'text-gray-700 dark:text-gray-300',
              'hover:border-gray-400 dark:hover:border-gray-500',
              'transition-all duration-300'
            )
          }}
          okText="Guardar"
          centered={false}
          className={clsx(
            'create-online-session-modal',
            theme === 'dark' ? 'dark-modal' : 'light-modal'
          )}
          style={{
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          maskStyle={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          wrapClassName={clsx(
            'create-online-session-modal-wrapper',
            theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
            'bottom-sheet-mobile'
          )}
        >
          <div className={clsx('flex flex-col space-y-4')}>
            <Input value={title} onChange={(e) => { setTitle(e.target.value); }} placeholder='Título' />
            <Input value={url} onChange={(e) => { setUrl(e.target.value); }} placeholder='Enlace' />
            <Select
              placeholder="Bloque"
              onChange={setEditedSubject}
              options={parsedSubjects}
              value={editedSubject}
            />
            <Select
              placeholder="Tema"
              onChange={setEditedTopic}
              options={parsedTopics}
              value={editedTopic}
            />
          </div>
        </Modal>
      )}

      {deleteModalVisible && (

        <Modal
          title={
            <div className={clsx(
              'flex items-center gap-3',
              'text-gray-900 dark:text-white'
            )}>
              <div className={clsx(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                'bg-gradient-to-br from-red-500 to-orange-500',
                'dark:from-red-600 dark:to-orange-600'
              )}>
                <IconAlertTriangle size={20} className="text-white" />
              </div>
              <span className="text-xl font-bold">Confirmar eliminación</span>
            </div>
          }
          open={deleteModalVisible}
          onCancel={handleDeleteCancel}
          onOk={handleDeleteConfirm}
          okButtonProps={{
            loading: deleting,
            danger: true,
            className: clsx(
              'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
              'border-none shadow-md hover:shadow-lg',
              'transition-all duration-300'
            )
          }}
          cancelButtonProps={{
            className: clsx(
              'border-gray-300 dark:border-gray-600',
              'text-gray-700 dark:text-gray-300',
              'hover:border-gray-400 dark:hover:border-gray-500',
              'transition-all duration-300'
            )
          }}
          okText="Eliminar"
          cancelText="Cancelar"
          centered={false}
          className={clsx(
            'delete-online-session-modal',
            theme === 'dark' ? 'dark-modal' : 'light-modal'
          )}
          style={{
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          maskStyle={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          wrapClassName={clsx(
            'delete-online-session-modal-wrapper',
            theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
            'bottom-sheet-mobile'
          )}
        >
          <p className={clsx(
            'text-gray-600 dark:text-gray-300',
            'text-base'
          )}>
            ¿Estás seguro de que quieres eliminar esta clase online? Esta acción no se puede deshacer.
          </p>
        </Modal>
      )}

    </Screen>
  );
};

export { OnlineSessionsPage };
